import {
  getStaticFlagValue,
  isStaticFlag,
} from '@/lib/featureFlags/staticFlags';

import { useFeatureFlags } from './useFeatureFlags';
import { useOptionalOrg } from './useOptionalOrg';

interface UseFeatureFlagOptions {
  /**
   * When true, the flag reads as `false` unless a fresh network fetch for the
   * current org has resolved successfully more recently than any failure.
   * `useFeatureFlags` seeds React Query with a persisted localStorage value
   * (`initialData`) stamped at epoch (`initialDataUpdatedAt: () => 0`), so
   * `data` is already populated while the first fetch is in flight and stays
   * populated if a refresh later fails. Without this option those stale/persisted
   * values can drive rendering.
   *
   * Use for gates that must fail closed — a feature that defaults off and may
   * only be revealed once the flag is confirmed on by a live response, so a
   * stale cache can never flash the gated variant. Defaults to `false` to
   * preserve the existing persisted-cache fast path for all other callers.
   */
  failClosedUntilFresh?: boolean;
  /**
   * Overrides the org read from `OrgContext`. Use this when the caller
   * already has `orgId` as a prop (e.g. a mid-wizard component) rather than
   * relying on the current org from context.
   */
  orgId?: number;
}

/**
 * Check if a feature flag is active for the current org.
 *
 * Static flags (baked into the deployed bundle) are evaluated client-side
 * with no network round-trip. Dynamic flags fall through to the cached
 * API result from useFeatureFlags.
 *
 * @param flagName - The name of the feature flag to check
 * @param options - Optional settings (e.g. `failClosedUntilFresh` to ignore
 *   persisted/stale values until a live fetch resolves)
 * @returns true if the flag is active, false otherwise (including while loading)
 *
 * @example
 * const isUserTimelineEnabled = useFeatureFlag('user_timeline_panel');
 * if (isUserTimelineEnabled) {
 *   // Show the feature
 * }
 */
export function useFeatureFlag(
  flagName: string,
  options: UseFeatureFlagOptions = {}
): boolean {
  const { failClosedUntilFresh = false } = options;
  // Read the org context optionally: an explicit-`orgId` caller may evaluate
  // a flag outside a TeamProvider (e.g. an embedded badge preview), where
  // useOrg() throws. Context is only the fallback source for the org id, so
  // its absence must fail closed (no org → no flags → false), never crash
  // the caller.
  const orgContext = useOptionalOrg();
  const orgId = options.orgId ?? orgContext?.currentOrg?.id;
  // Compute static-ness BEFORE invoking useFeatureFlags so we can suppress
  // the network query for static flags. The hook is still called every render
  // (Rules of Hooks), but `enabled: false` short-circuits the HTTP request
  // when nothing else on the page consumes the dynamic flag list.
  const isStatic = isStaticFlag(flagName);
  const {
    data: featureFlags,
    dataUpdatedAt,
    errorUpdatedAt,
  } = useFeatureFlags(orgId, {
    enabled: !isStatic,
  });

  if (isStatic) {
    return getStaticFlagValue(flagName);
  }

  if (!featureFlags) {
    return false;
  }

  // Fail closed unless the current value comes from a live fetch that succeeded
  // more recently than any failure:
  //   - `dataUpdatedAt` is 0 until a fetch succeeds (the epoch-stamped persisted
  //     `initialData` never advances it), so it stays 0 both while the first
  //     fetch is in flight and after a first fetch fails.
  //   - `errorUpdatedAt` advances on every fetch failure, including background
  //     refetches that keep prior data. When it trails the last success we hold
  //     the confirmed value; when it leads (an outage after a good fetch), we
  //     re-close so a stale-but-nonzero `dataUpdatedAt` cannot keep the gate on.
  if (
    failClosedUntilFresh &&
    (dataUpdatedAt === 0 || errorUpdatedAt > dataUpdatedAt)
  ) {
    return false;
  }

  return featureFlags.includes(flagName);
}
